import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLocale } from '../context/LocaleContext';
import { cssBackgroundImage, getProductHoverImageUrl, getProductImageUrl } from '../utils/imageUrl';
import './ProductCard.css';

function ProductCard({ product }) {
  const { formatMoney } = useLocale();
  const cardRef = useRef(null);
  const imageUrl = getProductImageUrl(product);
  const hoverUrl = getProductHoverImageUrl(product);
  const price = product.sale_price != null ? product.sale_price : product.price;
  const meta = product.team || product.sport || product.category;
  const onSale = product.sale_price != null;
  const isNew = Boolean(product.new_arrivals) && !onSale;

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return undefined;

    if (
      !('IntersectionObserver' in window) ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      el.classList.add('is-inview');
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('is-inview');
          observer.disconnect();
        }
      },
      { threshold: 0.08, rootMargin: '0px 0px -6% 0px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <article ref={cardRef} className="product-card">
      <Link to={`/product/${product.id}`} className="product-card-link">
        <div className="product-card-media">
          {onSale && <span className="product-card-badge">Sale</span>}
          {isNew && <span className="product-card-badge product-card-badge--quiet">New</span>}
          <div
            className="product-card-image"
            style={{ background: cssBackgroundImage(imageUrl) }}
          >
            {!imageUrl && <span>{product.category || product.sport || 'Product'}</span>}
          </div>
          {hoverUrl && (
            <div
              className="product-card-image product-card-image--hover"
              style={{ background: cssBackgroundImage(hoverUrl) }}
              aria-hidden="true"
            />
          )}
          <span className="product-card-view">View</span>
        </div>
        <div className="product-card-body">
          {meta ? <p className="product-card-meta">{meta}</p> : null}
          <h3 className="product-card-name">{product.name}</h3>
          <p className="product-card-price">
            {formatMoney(price)}
            {onSale && (
              <span className="product-card-price-was">{formatMoney(product.price)}</span>
            )}
          </p>
        </div>
      </Link>
    </article>
  );
}

export default ProductCard;
